'use client';

import classNames from 'classnames';
import {
  ForwardedRef,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import './css/Infotip.scss';

export type InfotipSize = 'sm' | 'lg';
export type InfotipPosition =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right';
export type InfotipStrategy = 'absolute' | 'fixed';

export interface InfotipProps extends HTMLAttributes<HTMLDivElement> {
  size?: InfotipSize;
  position?: InfotipPosition;
  autoPosition?: boolean;
  strategy?: InfotipStrategy;
  triggerRef: RefObject<HTMLButtonElement | null>;
  isOpen?: boolean;
  renderBody?: ReactNode;
  renderCta?: ReactElement;
  onOpenChange?: (isOpen: boolean) => void;
}

export const Infotip = forwardRef(
  (props: InfotipProps, outerRef: ForwardedRef<HTMLDivElement>) => {
    const {
      size = 'sm',
      position: positionProp = 'top',
      autoPosition = true,
      strategy = 'absolute',
      triggerRef,
      isOpen: isOpenProp = false,
      renderBody,
      renderCta,
      onOpenChange,
      ...spreadProps
    } = props;

    const [position, setPosition] = useState<InfotipPosition>(positionProp);
    const [isOpen, setIsOpen] = useState<boolean>(isOpenProp);
    const [isAriaLiveRendered, setIsAriaLiveRendered] = useState(false);

    const overlayRef = useRef<HTMLDivElement>(null);
    /* These refs prevent re-registration of event listeners. */
    const isOpenRef = useRef<boolean>(isOpen);
    const closeTimer = useRef<NodeJS.Timeout | null>(null);

    useImperativeHandle(outerRef, () => overlayRef.current!, []);

    const clearTimer = () => {
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
    };

    const openInfotip = () => {
      clearTimer();
      setIsOpen(true);
      onOpenChange?.(true);

      /* This Custom Event is used to close the Infotip when another opens on hover. */
      overlayRef.current?.dispatchEvent(
        new CustomEvent('psOpenChange', {
          bubbles: true,
          detail: { isOpen: true },
        })
      );
    };

    const closeInfotip = () => {
      setIsOpen(false);
      onOpenChange?.(false);

      overlayRef.current?.dispatchEvent(
        new CustomEvent('psOpenChange', {
          bubbles: true,
          detail: { isOpen: false },
        })
      );
    };

    const handleMouseEnter = () => clearTimer();

    const handleMouseLeave = () =>
      (closeTimer.current = setTimeout(() => closeInfotip(), 500));

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (isOpen && e.key === 'Escape') {
        /* Prevents the Escape key from closing a parent dialog or menu. */
        e.preventDefault();
        e.stopPropagation();
        closeInfotip();
      }
    };

    const handleFocusOut = (e: React.FocusEvent) => {
      const triggerElem = triggerRef.current;
      if (!triggerElem?.contains(e.relatedTarget as HTMLElement)) {
        closeInfotip();
      }
    };

    const positionInfotip = useCallback(() => {
      const triggerDomRect = triggerRef?.current?.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      if (!triggerDomRect) {
        /* Trigger ref is undefined or invalid. */
        return;
      }

      /* Sets the infotip position based on which quadrant of the viewport the trigger is in. */
      const newPositionY =
        triggerDomRect.y + triggerDomRect.height / 2 > windowHeight / 2
          ? 'top'
          : 'bottom';
      const newPositionX =
        triggerDomRect.x + triggerDomRect.width / 2 > windowWidth / 2
          ? 'left'
          : 'right';

      /* Uses the root font size to convert px values to rem. If px is used, the infotip may be
        positioned off-screen when users zoom or set a larger font size in their browser. */
      const rootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize
      );
      const triggerMidpointXInRem =
        (triggerDomRect.x + triggerDomRect.width / 2) / rootFontSize;
      const windowWidthInRem = windowWidth / rootFontSize;

      /* The max-width of the infotip is 18.75rem. Here, we check if the center of the trigger
        element is within 9.875rem of the viewport edge. Half of the infotip max-width is 9.375rem;
        thus, when the infotip is centered (top or bottom), 9.875rem allows a 0.5rem gap between
        the infotip and the edge of the viewport before position is shifted left or right. */
      if (
        triggerMidpointXInRem > 9.875 &&
        triggerMidpointXInRem < windowWidthInRem - 9.875
      ) {
        setPosition(newPositionY);
      } else {
        setPosition(`${newPositionY}-${newPositionX}`);
      }
    }, [triggerRef]);

    const setOverlayPosition = useCallback(() => {
      const triggerDomRect = triggerRef?.current?.getBoundingClientRect();
      const infotipDomRect = overlayRef.current!.getBoundingClientRect();
      const infotipPadding = size === 'sm' ? '0.5rem' : '1rem';
      /* Width of pointer is used to calculate horizontal offset. */
      const pointerWidth = size === 'sm' ? '0.375rem' : '0.625rem';

      if (!triggerDomRect) {
        /* Trigger ref is undefined or invalid. */
        console.warn(
          'Unable to set Infotip overlay position. Please ensure that you have passed a valid triggerRef.'
        );
        return;
      }

      /* Used to determine if the trigger is inside of a modal or overlay, in which case
        we should use offset instead of DOMRect for positioning. */
      const positionedAncestor = triggerRef.current?.offsetParent;
      const isSharedAncestor =
        positionedAncestor === overlayRef.current?.offsetParent;
      let newPositionTop: string | null = null;
      let newPositionLeft: string = 'auto';
      let newPositionRight: string = 'auto';
      let offsetX: number | null = null;
      let offsetY: number | null = null;

      if (
        positionedAncestor &&
        isSharedAncestor &&
        positionedAncestor.tagName !== 'BODY'
      ) {
        offsetX = triggerRef.current!.offsetLeft;
        offsetY = triggerRef.current!.offsetTop;
      } else {
        offsetX = triggerDomRect.x;
        offsetY = triggerDomRect.y;
      }

      const triggerMidpointX = offsetX + triggerDomRect.width / 2;

      switch (position) {
        case 'bottom':
          newPositionTop = `${offsetY + triggerDomRect.height}px`;
          newPositionLeft = `${triggerMidpointX - infotipDomRect.width / 2}px`;
          break;
        case 'bottom-right':
          newPositionTop = `${triggerDomRect.y + triggerDomRect.height}px`;
          newPositionLeft = `calc(${triggerMidpointX}px - ${infotipPadding} - ${pointerWidth})`;
          break;
        case 'bottom-left':
          newPositionTop = `${triggerDomRect.y + triggerDomRect.height}px`;
          newPositionRight = `calc(${
            window.innerWidth - triggerMidpointX
          }px - ${infotipPadding} - ${pointerWidth})`;
          break;
        case 'top':
          newPositionTop = `${offsetY - infotipDomRect.height}px`;
          newPositionLeft = `${triggerMidpointX - infotipDomRect.width / 2}px`;
          break;
        case 'top-right':
          newPositionTop = `${triggerDomRect.y - infotipDomRect.height}px`;
          newPositionLeft = `calc(${triggerMidpointX}px - ${infotipPadding} - ${pointerWidth})`;
          break;
        case 'top-left':
          newPositionTop = `${triggerDomRect.y - infotipDomRect.height}px`;
          newPositionRight = `calc(${
            window.innerWidth - triggerMidpointX
          }px - ${infotipPadding} - ${pointerWidth})`;
          break;
        default:
          break;
      }

      overlayRef.current!.style.top = newPositionTop!;
      overlayRef.current!.style.left = newPositionLeft;
      overlayRef.current!.style.right = newPositionRight;
    }, [position, size, triggerRef]);

    const setCtaTabindex = useCallback(() => {
      if (size === 'lg') {
        const focusableElements = ['a', 'button'];

        focusableElements.forEach((tag) => {
          const elements = overlayRef.current?.querySelectorAll(tag) ?? [];
          if (elements.length > 0) {
            elements.forEach((element) =>
              element.setAttribute('tabindex', isOpen ? '0' : '-1')
            );
          }
        });
      }
    }, [isOpen, size]);

    useEffect(() => setIsOpen(isOpenProp), [isOpenProp]);
    useEffect(() => setOverlayPosition(), [position, setOverlayPosition]);

    useEffect(() => {
      isOpenRef.current = isOpen;
      setCtaTabindex();
    }, [isOpen, setCtaTabindex]);

    useEffect(() => {
      if (autoPosition) {
        positionInfotip();
      }
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    useEffect(() => {
      const repositionInfotip = () => {
        if (autoPosition) {
          positionInfotip();
        }
        setOverlayPosition();
      };
      window.addEventListener('resize', repositionInfotip);

      return () => {
        window.removeEventListener('resize', repositionInfotip);
      };
    }, [autoPosition, positionInfotip, setOverlayPosition]);

    useEffect(() => {
      const triggerElem = triggerRef?.current;

      const handleTriggerKeyDown = (e: KeyboardEvent) => {
        if (isOpenRef.current && e.key === 'Escape') {
          /* Prevents the Escape key from closing a parent dialog or menu. */
          e.preventDefault();
          e.stopPropagation();
          closeInfotip();
        }
      };

      const handleTriggerFocusout = (e: FocusEvent) => {
        const isInfotipActionFocused = overlayRef.current?.contains(
          e.relatedTarget as HTMLElement
        );

        if (!isInfotipActionFocused) {
          closeInfotip();
        }
      };

      const handleExternalInfotipOpen = (e: Event) => {
        const customEvent = e as CustomEvent;
        const isOpenEvent = customEvent.detail.isOpen;
        const target = customEvent.target as HTMLElement;

        if (
          isOpenRef.current &&
          isOpenEvent &&
          target.className.includes('ps-infotip-overlay') &&
          target !== overlayRef.current
        ) {
          /* Another Infotip has opened. Close the current tip immediately. */
          closeInfotip();
        }
      };

      const handleOutsideClick = (e: Event) => {
        const elem = e.target as HTMLElement;
        const isInsideClick = overlayRef.current?.contains(elem);
        const isTriggerClick = triggerRef.current?.contains(elem);

        if (isOpenRef.current && !isInsideClick && !isTriggerClick) {
          closeInfotip();
        }
      };

      window.addEventListener('click', handleOutsideClick);

      if (size === 'sm') {
        triggerElem?.addEventListener('focusin', openInfotip);
        triggerElem?.addEventListener('focusout', closeInfotip);
        triggerElem?.addEventListener('keydown', handleTriggerKeyDown);
        triggerElem?.addEventListener('mouseenter', openInfotip);
        triggerElem?.addEventListener('mouseleave', handleMouseLeave);
        window.addEventListener('psOpenChange', handleExternalInfotipOpen);

        return () => {
          triggerElem?.removeEventListener('focusin', openInfotip);
          triggerElem?.removeEventListener('focusout', closeInfotip);
          triggerElem?.removeEventListener('keydown', handleTriggerKeyDown);
          triggerElem?.removeEventListener('mouseenter', openInfotip);
          triggerElem?.removeEventListener('mouseleave', handleMouseLeave);
          window.removeEventListener('click', handleOutsideClick);
          window.removeEventListener('psOpenChange', handleExternalInfotipOpen);
        };
      }

      if (size === 'lg') {
        triggerElem?.addEventListener('focusout', handleTriggerFocusout);
        triggerElem?.addEventListener('keydown', handleTriggerKeyDown);

        return () => {
          triggerElem?.removeEventListener('focusout', handleTriggerFocusout);
          triggerElem?.removeEventListener('keydown', handleTriggerKeyDown);
          window.removeEventListener('click', handleOutsideClick);
        };
      }
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [triggerRef]);

    useEffect(() => {
      const infotipId = overlayRef.current?.getAttribute('id');
      const triggerElem = triggerRef?.current;
      const triggerHasAriaDescribedOrLabelledby =
        triggerElem?.hasAttribute('aria-describedby') ||
        triggerElem?.hasAttribute('aria-labelledby');
      const triggerHasAriaExpanded = triggerElem?.hasAttribute('aria-expanded');
      const triggerHasAriaControls = triggerElem?.hasAttribute('aria-controls');

      if (!infotipId) {
        console.warn('You must pass an id to the Pulse Infotip component.');
        return;
      }

      if (size === 'sm' && !triggerHasAriaDescribedOrLabelledby) {
        console.warn(
          `The Infotip with id ${infotipId} must be used in conjunction with an approriately labelled trigger element. Please add an aria-describedby or aria-labelledby attribute (pointing to the Infotip id) to your trigger element.`
        );
      }

      if (
        size === 'lg' &&
        (!triggerHasAriaExpanded || !triggerHasAriaControls)
      ) {
        console.warn(
          `The Infotip with id ${infotipId} must be used in conjunction with an approriately labelled trigger element. Please ensure that your trigger element contains both aria-controls and aria-expanded attributes.`
        );
      }
    }, [size, triggerRef]);

    useEffect(() => {
      if (size === 'lg') {
        if (isOpen) {
          const timer = setTimeout(() => setIsAriaLiveRendered(true), 15);
          return () => clearTimeout(timer);
        } else {
          setIsAriaLiveRendered(false);
        }
      }
    }, [isOpen, size]);

    const overlayClasses = classNames({
      'ps-infotip-overlay': true,
      'ps-infotip-overlay-fixed': strategy === 'fixed',
      [`ps-infotip-overlay-padding-${size}`]: true,
      'ps-infotip-overlay-padding-top': position.includes('top'),
      'ps-infotip-overlay-padding-bottom': position.includes('bottom'),
    });

    const infotipClasses = classNames({
      'ps-infotip': true,
      [`ps-infotip--hidden`]: !isOpen,
      [`ps-infotip-${size}`]: true,
    });

    const pointerClasses = classNames({
      'ps-infotip-pointer': true,
      [`ps-infotip-pointer-${size}`]: true,
      [`ps-infotip-pointer-${position}`]: true,
    });

    return (
      <div
        className={overlayClasses}
        ref={overlayRef}
        role={size === 'sm' ? 'tooltip' : 'group'}
        tabIndex={
          /* Tabindex is needed for large variants to prevent close after clicking inert contents. */
          size === 'lg' ? -1 : undefined
        }
        aria-hidden={
          /* The aria-hidden attribute prevents double screenreader announcement for small variants. */
          size === 'sm' && !isOpen ? 'true' : undefined
        }
        {...spreadProps}
      >
        <div
          className={infotipClasses}
          onBlur={size === 'lg' ? (e) => handleFocusOut(e) : undefined}
          onKeyDown={size === 'lg' ? (e) => handleKeyDown(e) : undefined}
          onMouseEnter={size === 'sm' ? handleMouseEnter : undefined}
          onMouseLeave={size === 'sm' ? handleMouseLeave : undefined}
        >
          <div className={pointerClasses}></div>
          {renderBody}
          {renderCta}
        </div>

        {size === 'lg' && (
          <div
            className="ps-infotip-live-region ps-visually-hidden"
            aria-live="polite"
          >
            {isAriaLiveRendered && renderBody}
          </div>
        )}
      </div>
    );
  }
);
